import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { appPath } from "../../../utils/appPaths.js";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs, Btn } from "../../../AbstractElements";
import {
  Container,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import IncomeService from "../../../Services/income/IncomeService";
import InvoiceService from "../../../Services/invoices/InvoiceService";
import SpinnerLoader from "../../Loaders/Spinner";
import { toast } from "react-toastify";
import UpdateIncomeModal from "./UpdateIncomeModal";
import Swal from "sweetalert2";
import moment from "moment";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/webpack-resolver";
import {
  ComparisonScoreDisplay,
  IncomeSourceBadge,
  IncomeStatusBadge,
  formatIncomeAmount,
  formatReportingPeriod,
  groupYoutubeByReportingPeriod,
  runAfterDropdownClose,
} from "./IncomeListHelpers";
import IncomeRowActions from "./IncomeRowActions";
import IncomeListFilters from "./IncomeListFilters";
import IncomeYoutubeEmptyState from "./IncomeYoutubeEmptyState";
import YoutubePeriodBreakdown from "./YoutubePeriodBreakdown";
import YoutubeRevenueDispatchModal from "./YoutubeRevenueDispatchModal";
import "./income-list.scss";
import { getApiErrorMessage, toUserMessage } from "../../../utils/apiErrorMessage";

function IncomesContainer() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState(null);
  const [youtubeUrlsModalOpen, setYoutubeUrlsModalOpen] = useState(false);
  const [youtubeRevenueUrls, setYoutubeRevenueUrls] = useState([]);
  const [isYoutubeUrlsLoading, setIsYoutubeUrlsLoading] = useState(false);
  const [quarterlyInvoiceModal, setQuarterlyInvoiceModal] = useState(false);
  const [youtubeDispatchModalOpen, setYoutubeDispatchModalOpen] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const { isLoading, isError, data, error } = useQuery({
    queryKey: ["incomes"],
    queryFn: IncomeService.getUnifiedIncomes,
  });

  const incomes = Array.isArray(data?.data) ? data.data : [];

  const availableYears = useMemo(() => {
    const years = new Set();
    incomes.forEach((income) => {
      if (income.period_start) {
        const y = new Date(income.period_start).getFullYear();
        if (!Number.isNaN(y)) years.add(y);
      }
      if (income.period_end) {
        const y = new Date(income.period_end).getFullYear();
        if (!Number.isNaN(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [incomes]);

  const deleteIncomeMutation = useMutation({
    mutationFn: ({
      id,
      entity_id,
      source_type,
      youtube_type,
      delete_related_data,
    }) =>
      IncomeService.deleteIncome({
        id,
        entity_id,
        source_type,
        youtube_type,
        delete_related_data,
      }),
    onMutate: ({ id }) => {
      setDeletingIncomeIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    },
    onSuccess: (response) => {
      toast.success(response.message || "Income deleted successfully!");
      queryClient.invalidateQueries(["incomes"]);
    },
    onError: (mutationError) => {
      const status = mutationError.response?.status;
      const errorMessage = getApiErrorMessage(
        mutationError,
        "Failed to delete income"
      );

      if (status === 409) {
        toast.warn(errorMessage);
        return;
      }

      toast.error(`Failed to delete income: ${errorMessage}`);
      if (status === 500) {
        Swal.fire({
          title: "Error",
          text: "There was a server error while deleting the income. Ensure CSV processing has finished, then try again.",
          icon: "error",
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      if (!variables?.id) return;
      setDeletingIncomeIds((prev) => {
        if (!prev.has(variables.id)) return prev;
        const next = new Set(prev);
        next.delete(variables.id);
        return next;
      });
    },
  });

  const [processingIncomeIds, setProcessingIncomeIds] = useState(() => new Set());
  const [deletingIncomeIds, setDeletingIncomeIds] = useState(() => new Set());
  const lambdaPollTimersRef = useRef(new Map());

  const addProcessingIncome = useCallback((incomeId) => {
    setProcessingIncomeIds((prev) => {
      if (prev.has(incomeId)) return prev;
      const next = new Set(prev);
      next.add(incomeId);
      return next;
    });
  }, []);

  const removeProcessingIncome = useCallback((incomeId) => {
    setProcessingIncomeIds((prev) => {
      if (!prev.has(incomeId)) return prev;
      const next = new Set(prev);
      next.delete(incomeId);
      return next;
    });
  }, []);

  const isIncomeProcessing = useCallback(
    (incomeId) => processingIncomeIds.has(incomeId),
    [processingIncomeIds]
  );

  const isIncomeDeleting = useCallback(
    (incomeId) => deletingIncomeIds.has(incomeId),
    [deletingIncomeIds]
  );

  const clearLambdaPollTimer = useCallback((incomeId) => {
    const timerId = lambdaPollTimersRef.current.get(incomeId);
    if (timerId) {
      clearTimeout(timerId);
      lambdaPollTimersRef.current.delete(incomeId);
    }
  }, []);

  const clearLambdaPoll = useCallback(
    (incomeId) => {
      clearLambdaPollTimer(incomeId);
      removeProcessingIncome(incomeId);
    },
    [clearLambdaPollTimer, removeProcessingIncome]
  );

  const scheduleProcessCsvPoll = useCallback(
    (incomeId) => {
      clearLambdaPollTimer(incomeId);
      addProcessingIncome(incomeId);

      let attempt = 0;
      const maxAttempts = 20;

      const poll = () => {
        attempt += 1;
        if (attempt > maxAttempts) {
          clearLambdaPoll(incomeId);
          return;
        }

        const delayMs = attempt === 1 ? 30000 : 45000;
        const timerId = setTimeout(async () => {
          try {
            const response = await IncomeService.processCsvS3(incomeId);

            if (response.lambda_recently_triggered || response.lambda_triggered) {
              poll();
              return;
            }

            clearLambdaPoll(incomeId);

            if (response.success) {
              if (response.already_imported) {
                toast.info(response.message || "Data is already imported.");
              } else {
                toast.success(response.message || "CSV processed successfully!");
              }
              queryClient.invalidateQueries({ queryKey: ["incomes"] });
              queryClient.invalidateQueries({ queryKey: ["report"] });
              return;
            }

            toast.error(response.message || "Failed to process CSV");
          } catch {
            poll();
          }
        }, delayMs);

        lambdaPollTimersRef.current.set(incomeId, timerId);
      };

      poll();
    },
    [addProcessingIncome, clearLambdaPoll, clearLambdaPollTimer, queryClient]
  );

  useEffect(() => {
    const timers = lambdaPollTimersRef.current;
    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const processCsvMutation = useMutation({
    mutationFn: (incomeId) => IncomeService.processCsvS3(incomeId),
    onMutate: (incomeId) => addProcessingIncome(incomeId),
    onSuccess: (response, incomeId) => {
      if (response.lambda_recently_triggered) {
        toast.info(
          response.message ||
            "Lambda is still running. Checking again automatically…"
        );
        scheduleProcessCsvPoll(incomeId);
        return;
      }

      if (response.success) {
        if (response.already_imported) {
          removeProcessingIncome(incomeId);
          runAfterDropdownClose(() => {
            Swal.fire({
              title: "Already imported",
              text:
                response.message ||
                "This clean file is already in the database. Re-process to replace existing data.",
              icon: "info",
              showCancelButton: true,
              confirmButtonText: "Re-process now",
              cancelButtonText: "Close",
            }).then((result) => {
              if (result.isConfirmed) {
                reprocessCsvMutation.mutate(incomeId);
              }
            });
          });
          return;
        }

        if (response.lambda_triggered) {
          toast.info(
            response.message ||
              "Lambda started. Checking for output automatically…"
          );
          scheduleProcessCsvPoll(incomeId);
        } else {
          removeProcessingIncome(incomeId);
          toast.success(response.message || "CSV processed successfully!");
        }
        queryClient.invalidateQueries({ queryKey: ["incomes"] });
        queryClient.invalidateQueries({ queryKey: ["report"] });
        return;
      }

      removeProcessingIncome(incomeId);
      toast.error(toUserMessage(response, "Failed to process CSV"));
    },
    onError: (mutationError, incomeId) => {
      removeProcessingIncome(incomeId);
      toast.error(getApiErrorMessage(mutationError, "Failed to process CSV"));
    },
  });

  const reprocessCsvMutation = useMutation({
    mutationFn: (incomeId) =>
      IncomeService.processCsvS3(incomeId, { force_reprocess: true }),
    onMutate: (incomeId) => addProcessingIncome(incomeId),
    onSettled: (_data, _error, incomeId) => removeProcessingIncome(incomeId),
    onSuccess: async (response) => {
      if (response.success) {
        toast.success(response.message || "Income re-processed successfully!");
        await queryClient.invalidateQueries({ queryKey: ["incomes"] });
        await queryClient.invalidateQueries({ queryKey: ["report"] });
      } else {
        toast.error(toUserMessage(response, "Failed to re-process income"));
      }
    },
    onError: (mutationError) => {
      toast.error(getApiErrorMessage(mutationError, "Failed to re-process income"));
    },
  });

  const retryLambdaMutation = useMutation({
    mutationFn: (incomeId) => IncomeService.retryLambda(incomeId),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          toUserMessage(
            response.message,
            "Lambda re-triggered. Wait a few minutes, then process CSV again."
          )
        );
        queryClient.invalidateQueries(["incomes"]);
      } else {
        toast.error(toUserMessage(response, "Failed to re-trigger Lambda"));
      }
    },
    onError: (mutationError) => {
      toast.error(getApiErrorMessage(mutationError, "Failed to re-trigger Lambda"));
    },
  });

  const generateInvoicesMutation = useMutation({
    mutationFn: (options = {}) => InvoiceService.generateInvoices({ useQueue: false, ...options }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(
          `Invoice generation completed! Processed: ${response.labels_processed} label(s)${
            response.labels_failed > 0 ? `, Failed: ${response.labels_failed}` : ""
          }`
        );
      } else {
        toast.warning(toUserMessage(response, "Invoice generation completed with errors"));
      }
    },
    onError: (mutationError) => {
      toast.error(
        `Failed to generate invoices: ${getApiErrorMessage(mutationError, "Unknown error")}`
      );
    },
  });

  const matchesYearFilter = useCallback(
    (income) => {
      if (yearFilter === "all") return true;
      const targetYear = Number(yearFilter);
      const startYear = income.period_start
        ? new Date(income.period_start).getFullYear()
        : null;
      const endYear = income.period_end ? new Date(income.period_end).getFullYear() : null;
      return (
        startYear === targetYear ||
        endYear === targetYear ||
        (startYear != null && endYear != null && startYear <= targetYear && endYear >= targetYear)
      );
    },
    [yearFilter]
  );

  const filteredData = useMemo(
    () =>
      incomes.filter((income) => {
        const term = searchTerm.trim().toLowerCase();
        const matchesSearch =
          !term ||
          [
            income.title,
            income.artist_name,
            income.provider,
            income.month,
            income.description,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term));
        const matchesSource = sourceFilter === "all" || income.source_type === sourceFilter;

        return matchesSearch && matchesSource && matchesYearFilter(income);
      }),
    [incomes, searchTerm, sourceFilter, matchesYearFilter]
  );

  const youtubePeriodRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const baseRows = incomes.filter(
      (income) => income.source_type === "youtube" && matchesYearFilter(income)
    );
    const groups = groupYoutubeByReportingPeriod(baseRows);
    if (!term) return groups;

    return groups.filter((group) => {
      const periodLabel = formatReportingPeriod(group.period_start, group.period_end).toLowerCase();
      if (periodLabel.includes(term) || "youtube".includes(term)) return true;
      return group.rows.some((row) =>
        [row.artist_name, row.title, row.month]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      );
    });
  }, [incomes, searchTerm, matchesYearFilter]);

  const tableData = useMemo(() => {
    if (sourceFilter === "youtube") return youtubePeriodRows;
    if (sourceFilter === "streaming") return filteredData;

    const streamingRows = filteredData.filter((row) => row.source_type !== "youtube");
    if (youtubePeriodRows.length === 0) return streamingRows;

    const shapedYoutubeRows = youtubePeriodRows.map((group) => ({
      ...group,
      id: `yt-period-${group.id}`,
      provider: "YouTube",
      source_type: "youtube",
      isYoutubeGroup: true,
      currencies: group.currency || group.currencies || null,
    }));

    return streamingRows.concat(shapedYoutubeRows);
  }, [sourceFilter, filteredData, youtubePeriodRows]);

  const hasActiveFilters =
    Boolean(searchTerm.trim()) || yearFilter !== "all" || sourceFilter !== "all";

  const clearFilters = () => {
    setSearchTerm("");
    setYearFilter("all");
    setSourceFilter("all");
  };

  const summary = useMemo(() => {
    const rows = tableData;
    const total = rows.length;
    const streamingRows = rows.filter((row) => row.source_type !== "youtube");
    const youtubeRows = rows.filter((row) => row.source_type === "youtube");
    const done = rows.filter((row) => row.status === "done").length;

    const youtubeRevenue = youtubeRows.reduce(
      (sum, row) => sum + (Number(row.amount) || 0),
      0
    );
    const youtubeArtists = new Set(
      youtubeRows
        .flatMap((row) => (Array.isArray(row.artists) ? row.artists : []))
        .map((value) => String(value))
        .filter(Boolean)
    ).size;
    const youtubeCurrencies = [
      ...new Set(
        youtubeRows.map((row) => row.currency || row.currencies).filter(Boolean)
      ),
    ];

    return {
      total,
      streaming: streamingRows.length,
      youtube: youtubeRows.length,
      done,
      pending: total - done,
      streamingDone: streamingRows.filter((row) => row.status === "done").length,
      streamingPending: streamingRows.filter((row) => row.status !== "done").length,
      youtubeDone: youtubeRows.filter((row) => row.status === "done").length,
      youtubeRevenue,
      youtubeCurrency: youtubeCurrencies.length === 1 ? youtubeCurrencies[0] : null,
      youtubeArtists,
    };
  }, [tableData]);

  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("No file available for download");
      return;
    }
    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.download = fileName || "income_file";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleUpdateIncome = (income) => {
    if (income?.source_type === "youtube") {
      navigate(appPath(`/income/youtube/${income.youtube_type}/${income.entity_id}`));
      return;
    }
    setSelectedIncome(income);
    setIsUpdateModalOpen(true);
  };

  const handleDeleteIncome = (rowOrId) => {
    const row =
      rowOrId && typeof rowOrId === "object"
        ? rowOrId
        : { id: rowOrId, entity_id: rowOrId, source_type: "streaming" };
    const incomeId = row.id;

    if (isIncomeProcessing(incomeId)) {
      toast.warn(
        "CSV import is still running for this income. Wait for processing to finish before deleting."
      );
      return;
    }

    if (isIncomeDeleting(incomeId)) {
      return;
    }

    clearLambdaPoll(incomeId);

    const isYoutubeArtistRevenue =
      row.source_type === "youtube" && row.youtube_type === "artists_revenue";

    Swal.fire({
      title: "Delete income?",
      html: `
        <div style="text-align:left">
          <p>${
            isYoutubeArtistRevenue
              ? "Remove this YouTube revenue record from the system?"
              : "Remove this income record from the system?"
          }</p>
          <label style="display:flex;align-items:center;gap:8px;margin-top:12px">
            <input type="checkbox" id="delete-related-data" />
            <span>${
              isYoutubeArtistRevenue
                ? "Also delete related videos / country / source data for this month"
                : "Also delete related distribution data"
            }</span>
          </label>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      preConfirm: () => ({
        delete_related_data: document.getElementById("delete-related-data")?.checked,
      }),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteIncomeMutation.mutate({
          id: incomeId,
          entity_id: row.entity_id,
          source_type: row.source_type,
          youtube_type: row.youtube_type,
          delete_related_data: result.value.delete_related_data,
        });
      }
    });
  };

  const confirmAction = (options, onConfirm) => {
    runAfterDropdownClose(() => {
      Swal.fire(options).then((result) => {
        if (result.isConfirmed) onConfirm();
      });
    });
  };

  const handleProcessCsv = (incomeId) => {
    confirmAction(
      {
        title: "Process CSV?",
        text: "Start the S3 processing pipeline for this income.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Process",
      },
      () => processCsvMutation.mutate(incomeId)
    );
  };

  const handleReprocessCsv = (incomeId) => {
    confirmAction(
      {
        title: "Re-process income?",
        text: "Re-import the Lambda clean CSV into the database.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Re-process",
      },
      () => reprocessCsvMutation.mutate(incomeId)
    );
  };

  const handleCsvAction = (row, actionType) => {
    if (actionType === "reprocess") {
      handleReprocessCsv(row.id);
      return;
    }
    handleProcessCsv(row.id);
  };

  const handleRetryLambda = (incomeId) => {
    confirmAction(
      {
        title: "Retry Lambda?",
        text: "Re-trigger Lambda, then wait 5–10 minutes before processing CSV again.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Retry",
      },
      () => retryLambdaMutation.mutate(incomeId)
    );
  };

  const getMonthYearFromIncomeRow = (row) => {
    const monthStr = row?.month;
    if (monthStr && typeof monthStr === "string") {
      const match = monthStr.match(/^(\d{4})-(\d{2})/);
      if (match) {
        const monthNum = parseInt(match[2], 10);
        if (monthNum >= 1 && monthNum <= 12) {
          return { month: monthNum, year: match[1] };
        }
      }
    }
    if (row?.period_start) {
      const date = new Date(row.period_start);
      if (!Number.isNaN(date.getTime())) {
        return { month: date.getMonth() + 1, year: String(date.getFullYear()) };
      }
    }
    return null;
  };

  const handleGenerateYoutubeInvoice = (row) => {
    const monthYear = getMonthYearFromIncomeRow(row);
    if (!row.label_id) {
      toast.error("No label linked to this artist.");
      return;
    }
    if (!monthYear) {
      toast.error("Could not determine month/year for this income.");
      return;
    }
    const monthLabel = new Date(Number(monthYear.year), monthYear.month - 1, 1).toLocaleString(
      undefined,
      { month: "long", year: "numeric" }
    );
    confirmAction(
      {
        title: "Generate YouTube invoice?",
        html: `Create invoices for this artist's label for <strong>${monthLabel}</strong>.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Generate",
      },
      () =>
        generateInvoicesMutation.mutate({
          labelId: row.label_id,
          month: monthYear.month,
          year: monthYear.year,
        })
    );
  };

  const handleGenerateQuarterlyInvoices = () => {
    if (!selectedQuarter) {
      toast.error("Please select a quarter");
      return;
    }
    confirmAction(
      {
        title: "Generate quarterly invoices?",
        html: `Generate invoices for <strong>${selectedQuarter.toUpperCase()} ${selectedYear}</strong>.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Generate",
      },
      () => {
        generateInvoicesMutation.mutate({ quarter: selectedQuarter, year: selectedYear });
        setQuarterlyInvoiceModal(false);
      }
    );
  };

  const handleOpenYoutubeUrlsModal = async () => {
    setIsYoutubeUrlsLoading(true);
    try {
      const response = await IncomeService.getYoutubeRevenueUrls();
      setYoutubeRevenueUrls(Array.isArray(response?.data) ? response.data : []);
      setYoutubeUrlsModalOpen(true);
    } catch (mutationError) {
      toast.error(
        `Failed to load YouTube revenue URLs: ${getApiErrorMessage(
          mutationError,
          "Request failed"
        )}`
      );
    } finally {
      setIsYoutubeUrlsLoading(false);
    }
  };

  const handleExportYoutubeUrlsCsv = () => {
    if (!youtubeRevenueUrls.length) {
      toast.info("No YouTube revenue URLs to export.");
      return;
    }
    const headers = [
      "artist",
      "title",
      "youtube_id",
      "youtube_url",
      "month",
      "estimated_revenue",
      "currencies",
    ];
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const rows = youtubeRevenueUrls.map((row) =>
      [
        row.artist,
        row.title,
        row.youtube_id,
        row.youtube_url,
        row.month,
        row.estimated_revenue,
        row.currencies ?? "",
      ]
        .map(escapeCsv)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "youtube-revenue-urls.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const rowProcessingStyles = useMemo(
    () => [
      {
        when: (row) => processingIncomeIds.has(row.id),
        classNames: ["inc-row--working"],
      },
    ],
    [processingIncomeIds]
  );

  const streamingColumns = useMemo(
    () => [
      {
        name: "Provider",
        selector: (row) => row.provider,
        sortable: true,
        minWidth: "160px",
        grow: 1.2,
        cell: (row) => {
          const hasReport = row.source_type !== "youtube" && row.report_id;
          const isRunning = processingIncomeIds.has(row.id);
          const isYoutube = row.source_type === "youtube";
          const isYoutubeGroup = row.isYoutubeGroup === true;
          return (
            <div className="inc-title-cell">
              <div className="inc-title-cell__head">
                <div
                  className={`inc-title-cell__name ${
                    hasReport || isYoutube || isYoutubeGroup ? "inc-title-cell__name--link" : ""
                  }`}
                  onClick={() => {
                    if (isYoutubeGroup) {
                      setSourceFilter("youtube");
                      return;
                    }
                    if (isYoutube) {
                      handleUpdateIncome(row);
                      return;
                    }
                    if (hasReport) {
                      navigate(appPath(`/income/report/${row.report_id || row.id}`));
                    }
                  }}
                  title={row.provider || ""}
                >
                  {row.provider || "—"}
                </div>
                {row.visibility === false ? (
                  <span className="badge bg-secondary ms-1" title="Visible to Admins & Staff only">
                    Private
                  </span>
                ) : null}
                {isRunning ? (
                  <span className="inc-row-running-badge">
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                    Running
                  </span>
                ) : null}
              </div>
              {isYoutubeGroup ? (
                <div className="inc-title-cell__meta inc-title-cell__meta--period">
                  {row.artistCount} artist{row.artistCount === 1 ? "" : "s"} · Reporting period
                </div>
              ) : null}
              {row.description ? (
                <div
                  className="inc-title-cell__desc"
                  onClick={() =>
                    Swal.fire({ title: "Description", text: row.description, confirmButtonText: "Close" })
                  }
                >
                  View description
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        name: "Source",
        selector: (row) => row.source_type,
        sortable: true,
        width: "120px",
        minWidth: "120px",
        cell: (row) => <IncomeSourceBadge sourceType={row.source_type} />,
      },
      {
        name: "Amount",
        selector: (row) => row.amount,
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <span className="inc-amount">
            {row.amount == null ? "—" : formatIncomeAmount(row.amount)}
            {row.currencies ? <span className="inc-amount__currency">{row.currencies}</span> : null}
          </span>
        ),
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        width: "110px",
        minWidth: "110px",
        cell: (row) => <IncomeStatusBadge status={row.status} />,
      },
      {
        name: "Period",
        selector: (row) => row.period_start,
        sortable: true,
        minWidth: "190px",
        cell: (row) => {
          const start = moment(row.period_start);
          const end = moment(row.period_end);
          return start.isValid() && end.isValid() ? (
            <span className="inc-period">
              {start.format("MMM D, YYYY")} – {end.format("MMM D, YYYY")}
            </span>
          ) : (
            <span className="inc-period">{formatReportingPeriod(row.period_start, row.period_end)}</span>
          );
        },
      },
      {
        name: "Files",
        width: "90px",
        cell: (row) =>
          row.source_type === "youtube" ? (
            <span className="inc-muted-cell">—</span>
          ) : (
            <div className="inc-files">
              <button
                type="button"
                className="inc-file-btn"
                disabled={!row.file}
                onClick={() => handleDownloadFile(row.file, `${row.provider || "income"}_file`)}
                title="Download processed file"
              >
                <i className="fa fa-download" />
              </button>
              <button
                type="button"
                className="inc-file-btn inc-file-btn--raw"
                disabled={!row.raw_file}
                onClick={() => handleDownloadFile(row.raw_file, `${row.provider || "income"}_raw_file`)}
                title="Download raw file"
              >
                <i className="fa fa-file-text-o" />
              </button>
            </div>
          ),
      },
      {
        name: "Score",
        width: "90px",
        cell: (row) =>
          row.source_type === "youtube" ? (
            <span className="inc-score inc-score--pending">N/A</span>
          ) : row.report_id ? (
            <ComparisonScoreDisplay incomeId={row.id} reportId={row.report_id} />
          ) : (
            <span className="inc-score inc-score--pending">Pending</span>
          ),
      },
      {
        name: "Actions",
        width: "120px",
        minWidth: "120px",
        right: true,
        cell: (row) =>
          row.isYoutubeGroup === true ? (
            <button
              type="button"
              className="inc-period-open-btn"
              onClick={() => setSourceFilter("youtube")}
              title="Open the YouTube reporting-period view"
            >
              <i className="fa fa-youtube-play me-1" aria-hidden="true" />
              View artists
            </button>
          ) : (
            <IncomeRowActions
              row={row}
              onCsvAction={handleCsvAction}
              onRetry={handleRetryLambda}
              onViewReport={(_, reportId) =>
                navigate(appPath(`/income/report/${reportId || row.id}`))
              }
              onEdit={handleUpdateIncome}
              onDelete={handleDeleteIncome}
              onGenerateInvoice={handleGenerateYoutubeInvoice}
              isProcessPending={isIncomeProcessing(row.id)}
              isReprocessPending={isIncomeProcessing(row.id)}
              isDeletePending={isIncomeDeleting(row.id)}
              isRetryPending={retryLambdaMutation.isPending}
              isInvoicePending={generateInvoicesMutation.isPending}
            />
          ),
      },
    ],
    [
      navigate,
      setSourceFilter,
      processingIncomeIds,
      isIncomeProcessing,
      isIncomeDeleting,
      retryLambdaMutation.isPending,
      generateInvoicesMutation.isPending,
    ]
  );

  const youtubeColumns = useMemo(
    () => [
      {
        id: "platform",
        name: "Platform",
        selector: (row) => row.platform,
        sortable: true,
        width: "150px",
        minWidth: "150px",
        cell: () => (
          <span className="inc-source inc-source--youtube">
            <i className="fa fa-youtube-play me-1" aria-hidden="true" />
            YouTube
          </span>
        ),
      },
      {
        id: "period",
        name: "Reporting period",
        selector: (row) => row.period_start,
        sortable: true,
        minWidth: "230px",
        grow: 1,
        cell: (row) => (
          <span className="inc-period inc-period--range">
            {formatReportingPeriod(row.period_start, row.period_end)}
          </span>
        ),
      },
      {
        id: "artists",
        name: "Artists",
        selector: (row) => row.artistCount,
        sortable: true,
        width: "110px",
        minWidth: "110px",
        cell: (row) => (
          <span className="inc-yt-artists" title={(row.artists || []).join(", ")}>
            {row.artistCount}
          </span>
        ),
      },
      {
        id: "total",
        name: "Total income",
        selector: (row) => row.amount,
        sortable: true,
        minWidth: "170px",
        cell: (row) => (
          <span className="inc-amount inc-amount--youtube">
            {formatIncomeAmount(row.amount)}
            {row.currency ? <span className="inc-amount__currency">{row.currency}</span> : null}
          </span>
        ),
      },
      {
        id: "status",
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        width: "120px",
        minWidth: "120px",
        cell: (row) => <IncomeStatusBadge status={row.status} />,
      },
    ],
    []
  );

  const tableColumns = useMemo(() => {
    if (sourceFilter === "youtube") return youtubeColumns;
    if (sourceFilter === "streaming") {
      return streamingColumns.filter((column) => column.name !== "Source");
    }
    return streamingColumns;
  }, [sourceFilter, streamingColumns, youtubeColumns]);

  if (isLoading) return <SpinnerLoader />;
  if (isError) return <div className="text-danger p-4">Error loading incomes: {error.message}</div>;

  const isYoutubeView = sourceFilter === "youtube";
  const pageTitle = isYoutubeView ? "YouTube income" : sourceFilter === "streaming" ? "Streaming income" : "Income";

  const youtubeBreakdownProps = {
    onEdit: handleUpdateIncome,
    onDelete: handleDeleteIncome,
    onGenerateInvoice: handleGenerateYoutubeInvoice,
    onCsvAction: handleCsvAction,
    onRetry: handleRetryLambda,
    onViewReport: (row, reportId) => navigate(appPath(`/income/report/${reportId || row.id}`)),
    isIncomeProcessing,
    isIncomeDeleting,
    isRetryPending: retryLambdaMutation.isPending,
    isInvoicePending: generateInvoicesMutation.isPending,
  };

  const headerButtons = isYoutubeView
    ? [
        <Btn
          key="youtube-sync"
          attrBtn={{
            size: "sm",
            color: "primary",
            onClick: () => setYoutubeDispatchModalOpen(true),
          }}
        >
          <i className="fa fa-refresh me-1" />
          Sync YouTube
        </Btn>,
        <Btn
          key="youtube-urls"
          attrBtn={{
            size: "sm",
            color: "primary",
            outline: true,
            onClick: handleOpenYoutubeUrlsModal,
            disabled: isYoutubeUrlsLoading,
          }}
        >
          <i className="fa fa-youtube-play me-1" />
          {isYoutubeUrlsLoading ? "Loading…" : "Revenue URLs"}
        </Btn>,
      ]
    : [
        <Btn
          key="youtube-sync"
          attrBtn={{
            size: "sm",
            color: "primary",
            outline: true,
            onClick: () => setYoutubeDispatchModalOpen(true),
          }}
        >
          <i className="fa fa-youtube-play me-1" />
          Sync YouTube
        </Btn>,
        <Btn
          key="quarterly"
          attrBtn={{
            size: "sm",
            color: "primary",
            outline: true,
            onClick: () => setQuarterlyInvoiceModal(true),
            disabled: generateInvoicesMutation.isPending,
          }}
        >
          <i className="fa fa-file-text-o me-1" />
          Quarterly invoices
        </Btn>,
        <Btn
          key="add"
          attrBtn={{
            size: "sm",
            color: "primary",
            onClick: () => navigate(appPath("/income/income-new")),
          }}
        >
          <i className="fa fa-plus me-1" />
          Add streaming income
        </Btn>,
      ];

  return (
    <div className={`rd-income-list ${isYoutubeView ? "rd-income-list--youtube" : ""}`}>
      <Breadcrumbs
        parent="Income"
        title={pageTitle}
        mainTitle={pageTitle}
        buttons={headerButtons}
      />

      <Container fluid>
        <IncomeListFilters
          summary={summary}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          yearFilter={yearFilter}
          onYearChange={setYearFilter}
          availableYears={availableYears}
          sourceFilter={sourceFilter}
          onSourceChange={setSourceFilter}
          onReset={clearFilters}
          hasActiveFilters={hasActiveFilters}
          onSyncYoutube={() => setYoutubeDispatchModalOpen(true)}
          onViewYoutubeUrls={handleOpenYoutubeUrlsModal}
        />

        <div className="inc-panel">
          <div className="inc-table-wrap">
            {tableData.length > 0 ? (
              <DataTable
                columns={tableColumns}
                data={tableData}
                keyField="id"
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[10, 15, 25, 50]}
                highlightOnHover
                responsive
                conditionalRowStyles={rowProcessingStyles}
                {...(isYoutubeView
                  ? {
                      expandableRows: true,
                      expandOnRowClicked: true,
                      expandableRowsComponent: YoutubePeriodBreakdown,
                      expandableRowsComponentProps: youtubeBreakdownProps,
                      defaultSortFieldId: "period",
                      defaultSortAsc: false,
                    }
                  : {})}
              />
            ) : isYoutubeView && !hasActiveFilters ? (
              <IncomeYoutubeEmptyState
                onSync={() => setYoutubeDispatchModalOpen(true)}
                onViewUrls={handleOpenYoutubeUrlsModal}
              />
            ) : (
              <div className="inc-empty">
                {isYoutubeView
                  ? "No YouTube income matches your filters."
                  : "No income records match your filters."}
              </div>
            )}
          </div>
        </div>
      </Container>

      <UpdateIncomeModal
        isOpen={isUpdateModalOpen}
        toggle={() => {
          setIsUpdateModalOpen(false);
          setSelectedIncome(null);
        }}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["incomes"] })}
        income={selectedIncome}
      />

      <Modal isOpen={jsonModalOpen} toggle={() => setJsonModalOpen(!jsonModalOpen)} size="xl">
        <ModalHeader toggle={() => setJsonModalOpen(!jsonModalOpen)}>Report JSON</ModalHeader>
        <ModalBody>
          {selectedJsonData ? (
            <AceEditor
              mode="json"
              theme="github"
              value={JSON.stringify(selectedJsonData, null, 2)}
              readOnly
              width="100%"
              height="600px"
              fontSize={14}
              setOptions={{ showLineNumbers: true, tabSize: 2 }}
            />
          ) : (
            <div className="inc-empty">No data available</div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={youtubeUrlsModalOpen}
        toggle={() => setYoutubeUrlsModalOpen(!youtubeUrlsModalOpen)}
        size="xl"
      >
        <ModalHeader toggle={() => setYoutubeUrlsModalOpen(!youtubeUrlsModalOpen)}>
          YouTube revenue URLs ({youtubeRevenueUrls.length})
        </ModalHeader>
        <ModalBody>
          {youtubeRevenueUrls.length === 0 ? (
            <div className="inc-youtube-urls-empty">
              <p>No video revenue URLs found yet.</p>
              <p className="text-muted small mb-0">
                Run <strong>Sync YouTube</strong> for a connected artist and month. URLs appear
                after videos with estimated revenue are imported from ArtistSync.
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Artist</th>
                    <th>Title</th>
                    <th>Month</th>
                    <th>Revenue</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  {youtubeRevenueUrls.map((item, index) => (
                    <tr key={`${item.youtube_id}-${index}`}>
                      <td>{item.artist}</td>
                      <td>{item.title}</td>
                      <td>{item.month}</td>
                      <td>
                        {formatIncomeAmount(item.estimated_revenue)} {item.currencies ?? ""}
                      </td>
                      <td>
                        {item.youtube_url ? (
                          <a href={item.youtube_url} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleExportYoutubeUrlsCsv}>
            Export CSV
          </Button>
          <Button color="secondary" outline onClick={() => setYoutubeUrlsModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <YoutubeRevenueDispatchModal
        isOpen={youtubeDispatchModalOpen}
        toggle={() => setYoutubeDispatchModalOpen(!youtubeDispatchModalOpen)}
        onDispatched={() => queryClient.invalidateQueries({ queryKey: ["incomes"] })}
      />

      <Modal isOpen={quarterlyInvoiceModal} toggle={() => setQuarterlyInvoiceModal(!quarterlyInvoiceModal)}>
        <ModalHeader toggle={() => setQuarterlyInvoiceModal(!quarterlyInvoiceModal)}>
          Generate quarterly invoices
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="quarter">Quarter</Label>
            <Input
              type="select"
              id="quarter"
              value={selectedQuarter}
              onChange={(event) => setSelectedQuarter(event.target.value)}
            >
              <option value="">Select quarter</option>
              <option value="q1">Q1 (Jan – Mar)</option>
              <option value="q2">Q2 (Apr – Jun)</option>
              <option value="q3">Q3 (Jul – Sep)</option>
              <option value="q4">Q4 (Oct – Dec)</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="year">Year</Label>
            <Input
              type="number"
              id="year"
              value={selectedYear}
              onChange={(event) => setSelectedYear(event.target.value)}
              min="2020"
              max={new Date().getFullYear() + 1}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setQuarterlyInvoiceModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleGenerateQuarterlyInvoices}
            disabled={!selectedQuarter || !selectedYear || generateInvoicesMutation.isPending}
          >
            Generate
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default IncomesContainer;
